/**
 * Public audio API. This is the only module the rest of the game imports.
 *
 * The canonical design doc requires audio twice — "positive visual and audio
 * feedback" for Game 1's correct judgment, and "every correct or wrong action
 * has immediate visual and audio feedback" in the Overall Design Principles —
 * so this subsystem is a compliance requirement, not a garnish.
 *
 * CONTRACT
 * Everything here is safe to call at any time, in any order, on any device.
 * Before `initAudio()` has run from a user gesture (and on a browser with no
 * WebAudio at all, and after `disposeAudio()`) every function is a silent no-op
 * that returns normally. A game scene therefore never needs a try/catch, a
 * capability check, or a null guard around a sound.
 *
 * USAGE
 *
 *   import { initAudio, playSfx, startAmbience } from '../../audio';
 *
 *   // once, from any click/keydown — e.g. the "Start" button on the intro card
 *   startBtn.addEventListener('click', () => { void initAudio(); });
 *
 *   // anywhere thereafter
 *   playSfx('correct');
 *   playSfx('step', { volume: 0.6, rate: 1.08 });   // vary so it isn't robotic
 *   startAmbience('home');
 *
 * `initAudio()` is idempotent and cheap to call again, so the safest wiring is
 * to call it from *every* primary button handler rather than trying to find the
 * one true first gesture.
 *
 * AUDIT PANEL
 * `?debug=audio` opens a panel with a button for every cue (see `./demo`).
 */
import {
  disposeRig,
  initRig,
  isMutedInternal,
  isRunning,
  masterVolumeInternal,
  now,
  hasHeadroom,
  setMasterVolumeInternal,
  setMutedInternal,
  setOnReady,
  SCHEDULE_LEAD,
  clamp,
} from './context';
import { applyPendingAmbience, disposeAmbience, startAmbienceInternal, stopAmbienceInternal } from './ambience';
import { applyPendingMusic, stopGameMusic } from './samples';
export { startGameMusic, stopGameMusic, setMusicPaused, playGameSample } from './samples';

import { renderSfx, SFX_THROTTLE, type SfxId } from './sfx';

export type { SfxId } from './sfx';

export interface SfxOptions {
  /** Multiplies the cue's designed level. 1 = as authored. */
  volume?: number;
  /** Pitch/speed multiplier, like a sample playback rate. 1 = as authored. */
  rate?: number;
}

/**
 * Last trigger time per cue, so a cue driven by a physics event (footsteps, a
 * pile of bottles settling) cannot fire forty times in one frame. A plain
 * object with fixed keys — allocated once, never rebuilt.
 */
const lastAt: Record<SfxId, number> = {
  pickup: -1,
  inspect: -1,
  correct: -1,
  wrong: -1,
  step: -1,
  win: -1,
  lose: -1,
  tick: -1,
  uiClick: -1,
  uiOpen: -1,
  uiClose: -1,
  scan: -1,
  deposit: -1,
  bottleClink: -1,
};

/**
 * Lazily create and unlock the AudioContext. MUST be called from a user gesture.
 *
 * Resolves once a resume has been attempted — it never hangs waiting for a
 * gesture that may never arrive. If it was called too early, a one-shot
 * pointer/key listener picks up the next real gesture automatically.
 */
export async function initAudio(): Promise<void> {
  setOnReady(() => { applyPendingAmbience(); void applyPendingMusic(); });
  await initRig();
  applyPendingAmbience();
  void applyPendingMusic();
}

export function isAudioReady(): boolean {
  return isRunning();
}

/**
 * Fire and forget. No-op (never throws) if audio is unavailable or muted.
 */
export function playSfx(id: SfxId, opts?: SfxOptions): void {
  if (isMutedInternal() || !isRunning() || !hasHeadroom()) return;

  const t = now();
  const gap = SFX_THROTTLE[id];
  const prev = lastAt[id];
  if (gap > 0 && prev >= 0 && t - prev < gap) return;
  lastAt[id] = t;

  try {
    renderSfx(id, t + SCHEDULE_LEAD, clamp(opts?.volume ?? 1, 0, 4), clamp(opts?.rate ?? 1, 0.5, 2));
  } catch {
    // A sound is never worth breaking a frame for. Swallow and carry on.
  }
}

/** Mute persists to localStorage, so the choice survives a reload. */
export function setMuted(muted: boolean): void {
  setMutedInternal(muted);
}

export function isMuted(): boolean {
  return isMutedInternal();
}

/** 0..1. Defaults to a modest 0.55 — this plays in classrooms. */
export function setMasterVolume(v: number): void {
  setMasterVolumeInternal(v);
}

/** Current master volume, 0..1. */
export function masterVolume(): number {
  return masterVolumeInternal();
}

/** Gentle looping room tone; safe to call repeatedly. */
export function startAmbience(kind: 'home' | 'factory' | 'hub'): void {
  startAmbienceInternal(kind);
}

export function stopAmbience(): void {
  stopAmbienceInternal();
}

/** Full teardown: stops every bed, closes the AudioContext, drops listeners. */
export function disposeAudio(): void {
  stopGameMusic();
  disposeAmbience();
  disposeRig();
  for (const key of Object.keys(lastAt) as SfxId[]) lastAt[key] = -1;
}

/* ------------------------------------------------------------------ *
 * Audit panel hook
 * ------------------------------------------------------------------
 * Registered as a side effect of importing this module, so `?debug=audio` and
 * `window.__audioDemo()` work WITHOUT any edit to `main.ts` — the same trick
 * `render/textures.ts` uses to publish `window.__forgeDemo()`. The demo module
 * itself is dynamically imported so it never enters the production bundle's
 * critical path.
 * ------------------------------------------------------------------ */
{
  const w = globalThis as unknown as Record<string, unknown>;
  if (typeof window !== 'undefined') {
    if (!w.__audioDemo) {
      w.__audioDemo = async (): Promise<unknown> => (await import('./demo')).mountAudioDemo();
    }
    if (new URLSearchParams(window.location.search).get('debug') === 'audio') {
      void import('./demo').then((m) => m.mountAudioDemo());
    }
  }
}
